import * as React from "react";
import {
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoNotDisturbAltIcon from "@mui/icons-material/DoNotDisturbAlt";
import BlockIcon from "@mui/icons-material/Block";
import {useTranslation} from "react-i18next";
import {JobPosition, Order, SortableKey} from "../../../../hooks/useJobPositions";
import SortCell from "./SortCell";
import JobStatusChip from "../JobStatusChip";

export interface JobPositionsTableProps {
    loading: boolean;
    positions: JobPosition[];
    selectedId: string | null;
    onSelect: (id: string) => void;
    /** Number of branches each position (by name) is assigned to. */
    branchCounts: Record<string, number>;
    order: Order;
    orderBy: SortableKey;
    onSort: (field: SortableKey) => void;
    anchorEl: HTMLElement | null;
    menuOpen: boolean;
    menuRowId: string | null;
    handleMenuOpen: (event: React.MouseEvent<HTMLElement>, id: string) => void;
    handleMenuClose: () => void;
    handleMenuAction: (action: () => void) => void;
    handleEditOpen: (row: JobPosition) => void;
    handleApprove: (id: string) => void;
    handleReject: (id: string) => void;
    handleClose: (id: string) => void;
}

export default function JobPositionsTable({
    loading,
    positions,
    selectedId,
    onSelect,
    branchCounts,
    order,
    orderBy,
    onSort,
    anchorEl,
    menuOpen,
    menuRowId,
    handleMenuOpen,
    handleMenuClose,
    handleMenuAction,
    handleEditOpen,
    handleApprove,
    handleReject,
    handleClose,
}: Readonly<JobPositionsTableProps>): React.ReactElement {
    const {t} = useTranslation();
    return (
        <TableContainer sx={{
            border: "1px solid", borderColor: "divider", borderRadius: 1, textAlign: "left",
            "& .MuiTableCell-root:not(.MuiTableCell-alignRight):not(.MuiTableCell-alignCenter)": {textAlign: "left"},
            "& .MuiTableSortLabel-root": {justifyContent: "flex-start"},
        }}>
            <Table size="medium" aria-label="job positions table">
                <TableHead>
                    <TableRow sx={{bgcolor: "action.hover"}}>
                        <SortCell label={t("hr.jobs.table.header.position")} field="name" orderBy={orderBy}
                                  order={order} onSort={onSort}/>
                        <TableCell>{t("hr.jobs.table.header.branches")}</TableCell>
                        <SortCell label={t("hr.branches.table.header.status")} field="status" orderBy={orderBy}
                                  order={order} onSort={onSort}/>
                        <TableCell/>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading ? (
                        Array.from({length: 5}).map((_, i) => (
                            <TableRow key={`job-skeleton-${i}`}>
                                <TableCell><Skeleton variant="text"/></TableCell>
                                <TableCell><Skeleton variant="text" width={30}/></TableCell>
                                <TableCell><Skeleton variant="text" width={70}/></TableCell>
                                <TableCell><Skeleton variant="circular" width={28} height={28}/></TableCell>
                            </TableRow>
                        ))
                    ) : positions.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={4} align="center" sx={{py: 4}}>
                                <WorkOutlineIcon sx={{fontSize: 40, color: "text.disabled", mb: 1, display: "block", mx: "auto"}}/>
                                <Typography variant="body2" color="text.secondary">{t("hr.jobs.listing.empty")}</Typography>
                            </TableCell>
                        </TableRow>
                    ) : (
                        positions.map((row) => (
                            <TableRow
                                hover
                                key={row.id}
                                selected={row.id === selectedId}
                                onClick={() => onSelect(row.id)}
                                sx={{cursor: "pointer"}}
                            >
                                <TableCell sx={{fontWeight: 600}}>{row.name ?? "-"}</TableCell>
                                <TableCell>{branchCounts[row.name] ?? 0}</TableCell>
                                <TableCell><JobStatusChip row={row}/></TableCell>
                                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                    <IconButton
                                        size="small"
                                        aria-label="more"
                                        aria-controls={menuOpen && menuRowId === row.id ? `card-action-menu-${row.id}` : undefined}
                                        aria-haspopup="true"
                                        onClick={(e) => handleMenuOpen(e, row.id)}
                                    >
                                        <MoreHorizIcon fontSize="small"/>
                                    </IconButton>
                                    <Menu
                                        id={`card-action-menu-${row.id}`}
                                        anchorEl={anchorEl}
                                        open={menuOpen && menuRowId === row.id}
                                        onClose={handleMenuClose}
                                    >
                                        <MenuItem onClick={() => handleMenuAction(() => handleEditOpen(row))}>
                                            <ListItemIcon><EditIcon fontSize="small" color="primary"/></ListItemIcon>
                                            <ListItemText>{t("common.actions.edit")}</ListItemText>
                                        </MenuItem>
                                        <MenuItem onClick={() => handleMenuAction(() => handleApprove(row.id))}>
                                            <ListItemIcon><CheckCircleOutlineIcon fontSize="small" color="success"/></ListItemIcon>
                                            <ListItemText>{t("common.actions.approve")}</ListItemText>
                                        </MenuItem>
                                        <MenuItem onClick={() => handleMenuAction(() => handleReject(row.id))}>
                                            <ListItemIcon><DoNotDisturbAltIcon fontSize="small" color="error"/></ListItemIcon>
                                            <ListItemText>{t("common.actions.reject")}</ListItemText>
                                        </MenuItem>
                                        <MenuItem onClick={() => handleMenuAction(() => handleClose(row.id))}>
                                            <ListItemIcon><BlockIcon fontSize="small" sx={{color: "#e91e63"}}/></ListItemIcon>
                                            <ListItemText>{t("common.actions.close")}</ListItemText>
                                        </MenuItem>
                                    </Menu>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
